import {Button, Container, Group, NumberInput, Select, Stack, Stepper, Text, Title} from '@mantine/core';
import {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {Elements} from '@stripe/react-stripe-js';
import {loadStripe, StripeElementsOptions} from '@stripe/stripe-js';
import {PaymentElement, useElements, useStripe} from '@stripe/react-stripe-js';
import {CustomerSelector} from '../components/CustomerSelector';
import {FlowResultDisplay} from '../components/FlowResultDisplay';
import {FlowResult} from '../types';
import {add as createPaymentIntent} from '../../payment-intents/services/PaymentIntentsService';
import {PaymentIntent} from '../../payment-intents/types';
import {notifyError} from '../../../components/ServiceNotifications';
import config from '../../../config';

const stripePromise = loadStripe(config.stripe.publishableKey);

const currencies = ['usd', 'eur', 'gbp', 'cad', 'aud'].map(c => ({value: c, label: c.toUpperCase()}));

const PaymentForm = ({onResult}: { onResult: (result: FlowResult) => void }) => {
    const stripe = useStripe();
    const elements = useElements();

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!stripe || !elements) return;

        const {error, paymentIntent} = await stripe.confirmPayment({
            elements,
            confirmParams: {
                return_url: window.location.href,
            },
            redirect: 'if_required',
        });

        if (error) {
            onResult({success: false, error: error.message});
        } else {
            onResult({
                success: true,
                intentId: paymentIntent?.id,
                paymentMethodId: paymentIntent?.payment_method as string,
                status: paymentIntent?.status,
            });
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <PaymentElement/>
            <Button my="sm" type="submit" disabled={!stripe}>
                Pay Now
            </Button>
        </form>
    );
};

export const OneTimePaymentPage = () => {
    const navigate = useNavigate();
    const [active, setActive] = useState(0);
    const [customerId, setCustomerId] = useState<string | null>(null);
    const [amount, setAmount] = useState<number>(1000);
    const [currency, setCurrency] = useState<string>('usd');
    const [paymentIntent, setPaymentIntent] = useState<PaymentIntent | null>(null);
    const [creating, setCreating] = useState(false);
    const [result, setResult] = useState<FlowResult | null>(null);

    const handleCreatePaymentIntent = async () => {
        if (!customerId || !amount) return;
        setCreating(true);
        try {
            const pi = await createPaymentIntent({
                amount,
                currency,
                customerId,
                description: 'Payment Flow Tester - one-time payment',
            });
            console.log('Payment intent created:', pi);
            setPaymentIntent(pi as PaymentIntent);
            setActive(1);
        } catch (e) {
            notifyError(e);
        } finally {
            setCreating(false);
        }
    };

    const handleResult = (flowResult: FlowResult) => {
        setResult(flowResult);
        setActive(2);
    };

    const handleReset = () => {
        setActive(0);
        setCustomerId(null);
        setAmount(1000);
        setCurrency('usd');
        setPaymentIntent(null);
        setResult(null);
    };

    const options = paymentIntent?.clientSecret ? {
        clientSecret: paymentIntent.clientSecret,
        appearance: {
            theme: 'stripe',
            rules: {
                '.Input': {
                    border: '1px solid #E0E6EB',
                },
            },
        },
    } as StripeElementsOptions : undefined;

    return (
        <Container size="sm" py="xl">
            <Stack gap="xl">
                <div>
                    <Title order={3}>One-Time Payment</Title>
                    <Text c="dimmed" size="sm">
                        Create a payment intent and process a one-time charge.
                    </Text>
                </div>

                <Stepper active={active} allowNextStepsSelect={false}>
                    <Stepper.Step label="Payment Details"/>
                    <Stepper.Step label="Enter Card"/>
                    <Stepper.Step label="Result"/>
                </Stepper>

                {active === 0 && (
                    <Stack gap="md">
                        <CustomerSelector value={customerId} onChange={setCustomerId}/>
                        <NumberInput
                            label="Amount (in cents)"
                            placeholder="e.g. 1000 = $10.00"
                            value={amount}
                            onChange={v => setAmount(Number(v))}
                            min={50}
                        />
                        <Select
                            label="Currency"
                            data={currencies}
                            value={currency}
                            onChange={v => setCurrency(v || 'usd')}
                        />
                        <Group justify="space-between">
                            <Button variant="subtle" onClick={() => navigate('/payment-flow-tester')}>
                                Back
                            </Button>
                            <Button
                                onClick={handleCreatePaymentIntent}
                                disabled={!customerId || !amount}
                                loading={creating}
                            >
                                Create Payment Intent
                            </Button>
                        </Group>
                    </Stack>
                )}

                {active === 1 && options && (
                    <Elements stripe={stripePromise} options={options}>
                        <PaymentForm onResult={handleResult}/>
                    </Elements>
                )}

                {active === 2 && result && (
                    <FlowResultDisplay result={result} onReset={handleReset}/>
                )}
            </Stack>
        </Container>
    );
};
